/**
 * Thin Promise wrapper around the navigator.geolocation API.
 *
 * The browser API uses an old success/error callback shape — wrapping it
 * once here means callers can use async/await and lets us cleanly map the
 * legacy `PositionError.code` constants to typed reason strings.
 */
export interface Coords {
  latitude: number;
  longitude: number;
  accuracy: number;
}

export type GeolocationFailure = 'unavailable' | 'denied' | 'unknown';

export class GeolocationError extends Error {
  constructor(public readonly reason: GeolocationFailure) {
    super(`geolocation failed: ${reason}`);
    this.name = 'GeolocationError';
  }
}

const LAST_COORDS_KEY = 'mycity.lastCoords';
/** Positions older than this are ignored — a stale city is worse than none. */
const LAST_COORDS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Remember the most recent fix so features that shouldn't trigger their own
 * permission prompt (e.g. geo-fenced announcements) can still be
 * location-aware. Written only when the citizen has ALREADY granted
 * location for something they asked for.
 */
function rememberCoords(coords: Coords): void {
  try {
    localStorage.setItem(
      LAST_COORDS_KEY,
      JSON.stringify({ latitude: coords.latitude, longitude: coords.longitude, at: Date.now() }),
    );
  } catch {
    // Private mode / quota — location-awareness is a bonus, never required.
  }
}

/** Last known position, or null if we have none or it's stale. */
export function getLastKnownCoords(): { latitude: number; longitude: number } | null {
  try {
    const raw = localStorage.getItem(LAST_COORDS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { latitude?: number; longitude?: number; at?: number };
    if (typeof parsed.latitude !== 'number' || typeof parsed.longitude !== 'number') return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > LAST_COORDS_TTL_MS) return null;
    return { latitude: parsed.latitude, longitude: parsed.longitude };
  } catch {
    return null;
  }
}

/** Drop the remembered position — part of sign-out cleanup. */
export function forgetCoords(): void {
  try {
    localStorage.removeItem(LAST_COORDS_KEY);
  } catch {
    // ignore
  }
}

export function getCurrentCoords(timeoutMs = 10_000): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new GeolocationError('unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords: Coords = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        rememberCoords(coords);
        resolve(coords);
      },
      (err) => {
        const reason: GeolocationFailure =
          err.code === err.PERMISSION_DENIED
            ? 'denied'
            : err.code === err.POSITION_UNAVAILABLE
              ? 'unavailable'
              : 'unknown';
        reject(new GeolocationError(reason));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}
